// Listing-mode switcher for the Tech Log module — the MAJOR-module shape
// (src/blank/PATTERNS.md "Module weight").
//
// Tech Log's two listing views are ROUTES, not local state, mounted as
// siblings under this layout's <Outlet/>:
//
//   <base>/list       → TechLogDataGrid   (`<base>/logbook` redirects here)
//   <base>/dashboard  → TechLogOverview   (`<base>/overview` redirects here)
//
// Mirrors src/companies/CompaniesLayout.tsx, which itself mirrors prod's
// InventoryLayout/ComponentLayout breadcrumb dropdown (aeronetsoftware/cloud/
// aeronet-frontend @develop). This replaces a ViewModeHeaderSwitcher that held
// the mode in a `useViewMode` hook, so deep-links and back/forward now work.
//
// LOCAL ADAPTATION vs prod: prod keys its breadcrumb off ABSOLUTE paths
// ("/inventory/parts") because those modules mount at a fixed path. This one
// does not — App.tsx routes it at `menuItem.to + "/*"` from the backend nav,
// and mounts it at THREE aliases ("tech-log", "tech_log", "techlog"). One of
// them ("module-placeholder/Trend", on the sibling Trend module) is even two
// segments deep. Hardcoding any base would break the others, so the base is
// derived from the live pathname.
import { useEffect } from "react";

import { Box, Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";
import { migrateModeSegment } from "../components/renamedModeSegments";
import { ListingRouteBaseProvider } from "../components/datagrid/ListingRouteBase";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> dropdown label. Keys are the child route paths. Both were
 * RENAMED from the retired ViewModeHeaderSwitcher's keys ("logbook" /
 * "overview"). A stored "overview" is migrated to "dashboard" on read
 * (migrateModeSegment, H94). A stored "logbook" is NOT — it fails validation
 * and falls back to the default, which is "list" anyway.
 */
export const breadcrumbMenu: { [segment: string]: string } = {
  list: "List",
  dashboard: "Dashboard",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Technical log sheets",
  dashboard: "Release readiness and defects",
};

/** Matches the default mode — "list". */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * Unchanged from the `useViewMode("tech_log", …)` call this layout replaces.
 * The hook built its key as `${moduleKey}.viewMode` at runtime, so the literal
 * below must keep matching it or every stored mode resets on deploy. Note the
 * key is UNDERSCORED ("tech_log") while the directory is hyphenated — do not
 * normalise one to the other.
 */
export const MODE_STORAGE_KEY = "tech_log.viewMode";

/**
 * Is this a real mode segment? Uses hasOwnProperty, NOT `in` — `in` walks the
 * prototype chain, so "constructor" / "toString" / "valueOf" / "__proto__" all
 * test true against an object literal and would pass as valid modes.
 */
const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(breadcrumbMenu, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    // "overview" (the dashboard's name until H94) reads as "dashboard".
    stored = migrateModeSegment(window.localStorage.getItem(MODE_STORAGE_KEY));
  } catch {
    // Safari private mode and a quota-blocked browser both throw on access;
    // a mode redirect is not worth an unhandled exception. Same guard the
    // useViewMode hook this replaces carried.
    return DEFAULT_MODE_SEGMENT;
  }
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

interface TechLogLayoutProps {
  /** Module title from the nav (e.g. "Tech Log"); roots the breadcrumb. */
  title?: string;
}

const TechLogLayout: React.FC<TechLogLayoutProps> = ({ title }) => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "techLogBreadcrumbMenu",
  });

  // Split the live pathname so the trailing mode segment can be told apart
  // from the (nav-driven) module base. On "/tech-log/dashboard" that gives
  // basePath "/tech-log" and currentMode "dashboard".
  const segments = location.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const currentMode = onMode ? lastSegment : DEFAULT_MODE_SEGMENT;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//logbook", which a browser resolves as a PROTOCOL-RELATIVE
  // URL (host "logbook") and navigates off-site. Unreachable via App.tsx,
  // which always mounts under at least one segment, but reachable in tests.
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  const basePath = baseSegments.length ? "/" + baseSegments.join("/") : "";

  useEffect(() => {
    if (!onMode) return;
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, lastSegment);
    } catch {
      // Storage unavailable — the route still shows the right mode.
    }
  }, [onMode, lastSegment]);

  return (
    <Box height="100%">
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          <LinkRouter to={basePath || "/"}>{title || "Tech Log"} /</LinkRouter>
          <BreadcrumbHoverRoot popupState={popupState}>
            <Button
              endIcon={<BreadcrumbChevronIcon />}
              sx={breadcrumbCurrentTriggerSx}
            >
              <Typography
                variant="h2"
                component="span"
                sx={breadcrumbCurrentLabelSx}
              >
                {breadcrumbMenu[currentMode]}
              </Typography>
            </Button>
            <BreadcrumbDropdownMenu popupState={popupState}>
              {Object.entries(breadcrumbMenu).map(([segment, label]) => (
                <BreadcrumbMenuLinkItem
                  key={segment}
                  to={`${basePath}/${segment}`}
                  label={label}
                  description={describeMode(MODE_DESCRIPTIONS, segment)}
                  selected={currentMode === segment}
                  onClick={popupState.close}
                />
              ))}
            </BreadcrumbDropdownMenu>
          </BreadcrumbHoverRoot>
        </Title>
      </BreadcrumbHeaderListing>
      <ListingRouteBaseProvider value={{ base: basePath, createSegment: "create" }}>
        <Outlet />
      </ListingRouteBaseProvider>
    </Box>
  );
};

export default TechLogLayout;
